import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { fadeInUp, fadeIn, scaleIn } from '../utils/animations';

export const Hero = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = 0.7;
    }
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('nosotros');
    if (aboutSection) {
      aboutSection.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };

  return (
    <section
      ref={heroRef}
      id="inicio"
      className="relative w-full min-h-screen flex items-center overflow-hidden"
    >
      {/* Video Background con Parallax */}
      <motion.div 
        className="absolute inset-0 w-full h-full z-0"
        style={{ y }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/60 to-navy/70 z-10"></div>
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source
            src="https://assets.mixkit.co/videos/preview/mixkit-sailing-in-the-sea-during-sunset-1784-large.mp4"
            type="video/mp4"
          />
          <img
            src="https://images.unsplash.com/photo-1595880300291-5b28b0875e2b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
            alt="Río Guayas al atardecer"
            className="w-full h-full object-cover"
          />
        </video>
      </motion.div>

      {/* Decorative Animated Circles */}
      <motion.div
        className="absolute top-1/3 left-1/4 w-64 h-64 border border-gold/20 rounded-full"
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-40 h-40 border border-turquoise/30 rounded-full"
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.3, 0.7, 0.3],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />

      {/* Floating Particles - MÁS VISIBLES */}
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full bg-white shadow-lg"
          style={{
            width: Math.random() * 6 + 3 + 'px',
            height: Math.random() * 6 + 3 + 'px',
            boxShadow: '0 0 10px rgba(255, 255, 255, 0.8), 0 0 20px rgba(255, 255, 255, 0.4)',
          }}
          initial={{
            x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1920),
            y: typeof window !== 'undefined' ? window.innerHeight : 1080,
            scale: Math.random() * 0.5 + 0.8,
          }}
          animate={{
            y: [null, -150 - Math.random() * 300],
            opacity: [0, 0.9, 0.7, 0],
            scale: [0.8, 1.2, 1, 0.8],
          }}
          transition={{
            duration: Math.random() * 4 + 4,
            repeat: Infinity,
            delay: Math.random() * 4,
            ease: 'easeOut',
          }}
        />
      ))}

      {/* Hero Content */}
      <motion.div 
        className="container relative z-10 pb-24 md:pb-32"
        style={{ opacity }}
      >
        <div className="max-w-3xl mx-auto text-center text-white pt-20 md:pt-0">
          {/* Badge animado */}
          <motion.div
            className="inline-block mb-6 relative"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.span
              className="text-xs tracking-[0.3em] uppercase text-gold font-medium"
              animate={{
                textShadow: [
                  '0 0 10px rgba(255, 217, 61, 0.5)',
                  '0 0 20px rgba(255, 217, 61, 0.8)',
                  '0 0 10px rgba(255, 217, 61, 0.5)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Experiencia exclusiva en Guayaquil
            </motion.span>
            <motion.div
              className="h-px w-20 bg-gold/60 mx-auto mt-2"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
          </motion.div>

          {/* Main Title con animación de letras */}
          <motion.h1
            className="font-playfair text-4xl md:text-6xl lg:text-7xl font-bold mb-6 drop-shadow-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Vive Guayaquil desde el{' '}
            <motion.span
              className="text-gold italic inline-block"
              animate={{
                textShadow: [
                  '0 0 20px rgba(255, 217, 61, 0.3)',
                  '0 0 40px rgba(255, 217, 61, 0.6)',
                  '0 0 20px rgba(255, 217, 61, 0.3)',
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              río Guayas
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-lg md:text-xl mb-10 opacity-90 max-w-2xl mx-auto leading-relaxed px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Paseos exclusivos en lancha desde el Malecón 2000, para descubrir la
            ciudad desde una perspectiva privilegiada con atención personalizada.
          </motion.p>

          {/* CTA Button con efectos avanzados */}
          <motion.div
            className="flex justify-center mb-8 md:mb-12"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <motion.button
              onClick={scrollToAbout}
              className="relative group px-8 py-4 bg-transparent border-2 border-white rounded-full text-white font-medium text-base md:text-lg overflow-hidden"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Background animado en hover */}
              <motion.span
                className="absolute inset-0 bg-white"
                initial={{ x: '-100%' }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
              <span className="relative z-10 group-hover:text-navy transition-colors duration-300">
                Descubrir la experiencia
              </span>
            </motion.button>
          </motion.div>

          {/* Features Bar */}
          <motion.div
            className="flex items-center justify-center space-x-4 flex-wrap px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
          >
            <motion.div
              className="h-[1px] w-12 md:w-16 bg-white/40 hidden sm:block"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            />
            <p className="text-white/80 font-light text-sm md:text-base text-center">
              <span className="text-gold">Experiencia única</span> · Segura · Inolvidable
            </p>
            <motion.div
              className="h-[1px] w-12 md:w-16 bg-white/40 hidden sm:block"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.6, delay: 1.2 }}
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Decorative Wave - AJUSTADA MÁS ABAJO */}
      <motion.div
        className="absolute bottom-0 left-0 w-full z-10 -mb-1"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, delay: 0.5 }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          className="w-full h-auto"
          preserveAspectRatio="none"
          style={{ display: 'block' }}
        >
          <motion.path
            fill="#FFFFFF"
            fillOpacity="1"
            d="M0,224L48,213.3C96,203,192,181,288,181.3C384,181,480,203,576,213.3C672,224,768,224,864,208C960,192,1056,160,1152,154.7C1248,149,1344,171,1392,181.3L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 2, ease: 'easeInOut' }}
          />
        </svg>
      </motion.div>
    </section>
  );
};